import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import DatePicker from '@/components/common/DatePicker';

import { fetchMyProjects } from '@/service/api/project';

function getStatusConfig(status?: string) {
  const s = (status || '').toUpperCase();

  // 最新 11 种项目状态
  if (s === 'PENDING_CHANNEL_CONFIGURATION') {
    return { label: '待配置渠道', color: 'bg-amber-100 text-amber-800', type: 'pending' };
  }
  if (s === 'PENDING_CONTRACT_SIGNING') {
    return { label: '待签署合同', color: 'bg-amber-100 text-amber-800', type: 'pending' };
  }
  if (s === 'PENDING_PLATFORM_SIGNATURE') {
    return { label: '待平台盖章', color: 'bg-amber-100 text-amber-800', type: 'pending' };
  }
  if (s === 'CONTRACT_RESIGN_REQUIRED') {
    return { label: '需重签合同', color: 'bg-red-100 text-red-700', type: 'pending' };
  }
  if (s === 'CONTRACT_ARCHIVING') {
    return { label: '合同归档中', color: 'bg-blue-100 text-blue-700', type: 'pending' };
  }
  if (s === 'PREPARING_RELEASE') {
    return { label: '发行准备中', color: 'bg-sky-100 text-sky-700', type: 'active' };
  }
  if (s === 'PUBLISHING') {
    return { label: '发行发布中', color: 'bg-indigo-100 text-indigo-700', type: 'active' };
  }
  if (s === 'OPERATING') {
    return { label: '运营中', color: 'bg-emerald-100 text-emerald-700', type: 'active' };
  }
  if (s === 'PAUSED') {
    return { label: '已暂停', color: 'bg-gray-100 text-gray-700', type: 'paused' };
  }
  if (s === 'OFFLINE') {
    return { label: '已下架', color: 'bg-gray-200 text-gray-600', type: 'offline' };
  }
  if (s === 'TERMINATED') {
    return { label: '已终止', color: 'bg-red-50 text-red-600', type: 'terminated' };
  }

  // 兼容老数据与兜底
  if (s === 'ACTIVE' || s === 'RUNNING' || s === 'PUBLISHED') {
    return { label: '进行中', color: 'bg-primary-100 text-primary-700', type: 'active' };
  }
  if (s === 'PENDING' || s === 'PENDING_REVIEW' || s === 'IN_AUDIT') {
    return { label: '待处理', color: 'bg-accent-100 text-accent-700', type: 'pending' };
  }
  if (s === 'COMPLETED' || s === 'FINISHED') {
    return { label: '已完成', color: 'bg-secondary-100 text-secondary-700', type: 'completed' };
  }
  if (s === 'DISPUTED' || s === 'CANCELLED') {
    return { label: '已终止', color: 'bg-red-50 text-red-600', type: 'terminated' };
  }

  return { label: status || '未知状态', color: 'bg-primary-100 text-primary-700', type: 'active' };
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr.substring(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const statusTabs = [
  { key: '', label: '全部' },
  { key: 'PREPARING_RELEASE', label: '准备中' },
  { key: 'PUBLISHING', label: '发布中' },
  { key: 'OPERATING', label: '运营中' },
  { key: 'PAUSED', label: '已暂停' },
  { key: 'OFFLINE', label: '已下架' },
  { key: 'TERMINATED', label: '已终止' },
];

export default function ProjectsPage() {
  const [status, setStatus] = useState<string>('');
  const [keyword, setKeyword] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const [projects, setProjects] = useState<Api.Project.ProjectResponse[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const lastFetchKeyRef = useRef<string>('');

  const loadProjects = useCallback(async (force = false) => {
    const requestKey = `${status}_${keyword}_${startDate}_${endDate}`;
    if (!force && lastFetchKeyRef.current === requestKey) {
      return;
    }
    lastFetchKeyRef.current = requestKey;

    setIsLoading(true);
    setErrorMessage('');
    try {
      const params: Api.Project.QueryProjectsParams = {
        page: 1,
        page_size: 100
      };
      if (status) params.status = status;
      if (keyword.trim()) params.keyword = keyword.trim();
      if (startDate) params.updated_start = startDate;
      if (endDate) params.updated_end = endDate;

      const res = await fetchMyProjects(params);
      setProjects(res?.items || []);
      setTotalCount(res?.total ?? (res?.items?.length || 0));
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : '获取项目列表失败');
      lastFetchKeyRef.current = '';
    } finally {
      setIsLoading(false);
    }
  }, [status, keyword, startDate, endDate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadProjects();
    }, 300);
    return () => clearTimeout(timer);
  }, [loadProjects]);

  const handleStartDateChange = (val: string) => {
    setStartDate(val);
    if (val && endDate && val > endDate) {
      setEndDate('');
    }
  };

  const handleEndDateChange = (val: string) => {
    setEndDate(val);
    if (val && startDate && val < startDate) {
      setStartDate('');
    }
  };

  const activeCount = useMemo(() => {
    return projects.filter((p) => getStatusConfig(p.status).type === 'active').length;
  }, [projects]);

  const pendingCount = useMemo(() => {
    return projects.filter((p) => getStatusConfig(p.status).type === 'pending' || !!p.status_remark).length;
  }, [projects]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <div className="pt-28 sm:pt-32">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading font-bold text-xl text-foreground-950">发行项目</h1>
              <p className="text-xs text-foreground-500 mt-1">
                管理签约后的发行项目，跟踪渠道数据与收益结算
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/applications"
                className="px-4 py-2 rounded-lg border border-primary-300/60 text-primary-700 text-sm font-medium hover:bg-primary-50 transition-all cursor-pointer whitespace-nowrap"
              >
                查看申请记录
              </Link>
              <Link
                to="/apply"
                className="px-5 py-2 rounded-lg bg-primary-500 text-foreground-950 text-sm font-semibold hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap"
              >
                新建发行申请
              </Link>
            </div>
          </div>

          {/* Stats Cards (恢复原版UI) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="glass-card rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-foreground-950">{totalCount}</p>
              <p className="text-xs text-foreground-500 mt-1">全部项目</p>
            </div>
            <div className="glass-card rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-primary-600">{activeCount}</p>
              <p className="text-xs text-foreground-500 mt-1">进行中</p>
            </div>
            <div className="glass-card rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-accent-600">{pendingCount}</p>
              <p className="text-xs text-foreground-500 mt-1">待处理</p>
            </div>
            <div className="glass-card rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-foreground-500">-</p>
              <p className="text-xs text-foreground-500 mt-1">累计收益</p>
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-xs text-red-600 flex items-center justify-between">
              <span>{errorMessage}</span>
              <button onClick={() => loadProjects(true)} className="underline hover:text-red-700 cursor-pointer">重新加载</button>
            </div>
          )}

          {/* Status Filter与扩展搜索 (恢复原版圆角按钮UI) */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            {/* 原版状态按钮 */}
            <div className="flex flex-wrap items-center gap-2">
              {statusTabs.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setStatus(s.key)}
                  className={`text-xs px-3.5 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    status === s.key
                      ? 'bg-foreground-950 text-background-50 font-medium'
                      : 'border border-background-300/60 text-foreground-500 hover:bg-gray-50'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* 接口搜索与更新时间控件 */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative w-44 sm:w-52">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs" />
                <input
                  type="text"
                  placeholder="搜索项目编号或名称..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full h-8 pl-8 pr-3 rounded-lg border border-background-300 bg-white text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div className="flex items-center gap-1 text-xs">
                <div className="w-28 sm:w-32">
                  <DatePicker
                    value={startDate}
                    onChange={handleStartDateChange}
                    placeholder="更新开始"
                    maxDate={endDate || undefined}
                    size="sm"
                    clearable
                  />
                </div>
                <span className="text-foreground-400">-</span>
                <div className="w-28 sm:w-32">
                  <DatePicker
                    value={endDate}
                    onChange={handleEndDateChange}
                    placeholder="更新结束"
                    minDate={startDate || undefined}
                    size="sm"
                    clearable
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Projects List */}
          {isLoading ? (
            <div className="text-center py-20">
              <div className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-sm text-foreground-500">项目列表加载中...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {projects.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500 mb-3">暂无发行项目</p>
                  <Link
                    to="/apply"
                    className="text-sm text-primary-600 hover:text-primary-500 cursor-pointer font-medium inline-flex items-center gap-1"
                  >
                    <span>新建发行申请</span>
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ) : (
                projects.map((project) => {
                  const statusInfo = getStatusConfig(project.status);
                  const displayId = project.project_no || project.id || '-';
                  return (
                    <Link
                      key={project.id}
                      to={`/projects/${project.id}`}
                      className="glass-card rounded-xl p-5 hover:border-primary-200/50 transition-all cursor-pointer block"
                    >
                      <div className="flex items-center justify-between gap-4">
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
                            <h3 className="text-sm font-semibold text-foreground-950">{project.title || '发行项目'}</h3>
                            <span className="text-xs text-foreground-400 font-mono bg-gray-100/80 px-2 py-0.5 rounded">
                              {displayId}
                            </span>
                            {project.status_remark && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 flex-shrink-0">
                                {project.status_remark}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400">
                            <span>{project.total_episode_count || 0} 集</span>
                            <span>创建于 {formatDate(project.created_at)}</span>
                            {project.updated_at && <span>更新于 {formatDate(project.updated_at)}</span>}
                            {project.owner_subject_name && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-foreground-500">
                                {project.owner_subject_name}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Status */}
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                          <i className="ri-arrow-right-s-line text-foreground-300"></i>
                        </div>
                      </div>
                    </Link>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}